namespace Core.Flights {

    export enum DetailsResponseCode {
        Success,
        Unavailable
    }

    export interface DetailsRequest {
        AvailabilitySearchId: string;
        FlightId: string;
        IncludeSeatPlan: boolean;
        MultiSector: boolean;
        IncludeBrandedFares?: boolean;
    }

    export interface Range<T> {
        Min: T;
        Max: T;
    }

    export interface Destination {
        CountryId: number;
        ProvinceId: number;
        LocationId: number;
        EstablishmentId: number;
        PolygonId: number;
    }

    export interface FilterCriteria {
        DepartureAirport: string[];
        Airline: string[];
        Stop: number[];
        OutboundTakeOffRange: Range<number>;
        OutboundLandingRange: Range<number>;
        ReturnTakeOffRange: Range<number>;
        ReturnLandingRange: Range<number>;
        PriceRange: Range<number>;
        HideAlternativeDates: boolean;
        DepartureDates: string[];
    }

    export interface SearchRequest {
        TripId: string;
        Adults: number;
        ChildAges: number[];
        AvailabilitySearchId: string;
        Cabin: Cabin;
        DepartureAirports: string[];
        DestinationAirports: string[];
        DepartureDate: Date;
        Destination: Destination;
        FilterCriteria: FilterCriteria;
        FlightOnlySearch: boolean;
        IsDirectFlight: boolean;
        OneWayOnly: boolean;
        Paging: Paging;
        PreferredCarrierCodes: string[];
        ReturnDate?: Date;
        SortCriterion: SortCriterion;
    }

    export enum SortCriterion {
        None,
        PriceLowToHigh,
        PriceHighToLow,
        Airline,
        DepartureTimeEarlyToLate,
        DepartureTimeLateToEarly,
        ReturnTimeEarlyToLate,
        ReturnTimeLateToEarly,
        TotalJourneyTimeLongestFirst,
        TotalJourneyShortestLongestFirst,
        Convenience
    }

    export interface Paging
    {
        PageIndex: number;
        PageSize: number;
    }

    export interface SearchResponse {
        AvailabilitySearchId: string;
        CombinedDates: AvailabilityGroup;
        FilterCriteria: FilterCriteria;
    }

    export interface AvailabilityGroup {
        AvailableFlights: Flight[];
        UnfilteredAggregated: AvailabilityAggregates;
        FilteredAggregated: AvailabilityAggregates;
        Aggregated: AvailabilityAggregates;
    }

    export interface AvailabilityAggregates {
        TotalNumberOfFlights: number;
        FilteredNumberOfFlights: number;
        FlightsByDepartureAirportTitles: { [key:string]:MinPriceAggregateInfo };
        FlightsByAirline: { [key:string]:AirlineAggregateInfo };
        FlightsByStops: { [key:string]:MinPriceAggregateInfo };
        PriceRange: Range<number>;
        OutboundTakeOffRange: Range<number>;
        ReturnTakeOffRange: Range<number>;
        DepartureDates: { [key:string]:MinPriceAggregateInfo };
    }

    interface AggregateInfo {
        Count: number;
    }

    export interface MinPriceAggregateInfo extends AggregateInfo {
        MinumumPrice: number;
    }

    export interface AirlineAggregateInfo extends MinPriceAggregateInfo {
        Logo: string;
    }

    export interface Flight extends Itinerary {
        FlightId: string;
        DetailsId?: string;
        CurrencyCode: string;
        LeadAdultPrice: number;
        LeadAdultTax: number;
        FlightPlusChargePerPerson: number;
        LeadBaggagePrice: number;
        AllBagsOrNone: boolean;
        PriorityForPreSelection: boolean;
    }

    export interface Details {
        ResponseCode : DetailsResponseCode;
        CanSelectSeats: boolean;
        DetailsId: string;
        Itinerary: Itinerary;
        AdditionalInformation: string;
        OptionConditions: { [key:string]:string };
        FlightSectorOptionalExtras: FlightSectorOptionalExtras[];
        SupplierFareRules:string;
    }

    export enum FareType {
        NotSpecified,
        Published,
        Private,
        Package
    }

    export interface Itinerary {
        HasFullDates: boolean;
        IsOriginOpenJaw: boolean;
        Legs: Leg[];
        Nights: number;
        AirlineImages: string[];
        DepartureDate: string;
        FareType: FareType;
        FareTypeName: string;
    }

    export interface FlightSectorOptionalExtras {
        ArrivalAirport: string;
        DepartureAirport: string;
        Description: string;
        OptionalExtras: OptionalExtraGroup[];
        CheckInOptions: OptionalExtraGroup[];
        NonCheckInOptions: OptionalExtraGroup[];
    }

    export interface OptionalExtraGroup {
        Title: string;
        Type: OptionalExtraGroupType;
        MultiOptionalExtras: MultiOptionalExtra[];
        SingleOptionalExtras: OptionalExtra[];
    }

    export interface MultiOptionalExtra {
        Description: string;
        Type: string;
        Options: OptionalExtra[];
    }

    export interface OptionalExtra {
        Key: OptionalExtraKey;
        OptionId: string;
        Description: string;
        Type: string;
        UnitAmount: number;
        UnitAmountString: string;
        TotalAmountString: string;
        Quantity: number;
        IsDefault: boolean;
        UnitWeightKg: number;
    }

    export interface OptionalExtraKey {
        OptionId: string;
        Quantity: number;
    }

    export enum OptionalExtraGroupType
    {
        Baggage,
        Meal,
        Other,
        SpecialBaggage,
        CheckIn
    }

    export enum Cabin
    {
        NotSpecified,
        Economy,
        PremiumEconomy,
        First,
        Business
    }

    export interface Leg {
        WifiAvailable: boolean;
        Cabin: Cabin;
        CabinType: string;
        DurationMinutes: number;
        Duration: string;
        DurationSummary: string;
        DayDiff: string;
        IsOutboundLeg: boolean;
        LegType: string;
        Segments: Segment[];
        Nights: number;
        DepartureAirport: AirportSummary;
        ArrivalAirport: AirportSummary;
        IsOriginOpenJaw: boolean;
        IsMixedCarrier: boolean;
        ViaInfo: ViaInfo[];
        NumberOfStops: number;
        ArrivalDate: Date;
        DepartureDate: Date;
        RawDisplayDate: string;
        DisplayDate: BindableDate;
        BagsIncludedCount: number;
        HandBaggageInformation: HandBaggageInformation;
        FareType: FareType;
        FareTypeName: string;
        LegIndex: number;
    }

    export interface Segment {
        CarrierName: string;
        FlightNumber: string;
        DepartureDate: string;
        ArrivalDate: string;
        AdditionalStops: number;
        CodeshareAirlineName: string;
        DepartureAirportTitle: string;
        DepartureAirportCode: string;
        DestinationAirportTitle: string;
        DestinationAirportCode: string;
        Logo: string;
        DestinationTerminal: string;
        DepartureTerminal: string;
        Cabin: Cabin;
        CabinType: string;
        SupplierFareClass: string;
        ZonedDepartureDate: string;
        ZonedArrivalDate: string;
        Duration: string;
        Connection: SegmentConnection;
        DayDiff: string;
    }

    export interface SegmentConnection {
        DurationMinutes: number;
        Duration: string;
        InfoIsComplete: boolean;
    }

    export interface AirportSummary {
        Title: string;
        Code: string;
    }

    export interface ViaInfo {
        AirportTitle: string;
        AirportCode: string;
        ArrivalDate: Date;
        DepartureDate: Date;
        FlightNumber: string;
        AdditionalStops: number;
        HideTimes: boolean;
    }

    export interface BindableDate {
        e: string;
        d: number;
        m: string;
        y: number;
    }

    export interface HandBaggageInformation {
        PiecesAllowed: number;
        WidthCm: number;
        HeightCm: number;
        DepthCm: number;
        WeightKg: number;
    }

    export interface DepartureDatesResponse {
        DepartureDates : string[];
    }
}
